import { Box, capitalize, Hidden, useTheme } from "@material-ui/core";
import { Rating } from "@material-ui/lab";
import { navigate } from "gatsby";
import moment from "moment";
import React, { useCallback, useState } from "react";
import fastCompare from "react-fast-compare";
import { Helmet } from "react-helmet";
import { useInView } from "react-intersection-observer";
import OilAromaIcon from "../../../components/oil-aroma-icon";
import OilEffectIcon from "../../../components/oil-efffect-icon";
import SeparatorBullet from "../../../components/separator-bullet";
import { formatTotalPercentage } from "../../../helpers/coa-report";
import { formatPrice } from "../../../helpers/price";
import {
  getDefaultProductItemVariant,
  getProductVariantWeightLabel
} from "../../../helpers/product";
import { getProductDetailsPath } from "../../../helpers/url";
import { PodHistoryType } from "../../../hooks/use-activated-pod-history";
import useFeatureFlag from "../../../hooks/use-feature-flag";
import useTranslate from "../../../hooks/use-translate";
import { analyticsTrackProductClick } from "../../../services/analytics";
import {
  PriceType,
  ProductListItemType,
  ProductVariantListItemType
} from "../../../types/airgraft";
import { ProductAvailabilityType } from "../../../types/airgraft-express";
import PodPackageImage from "../PodPackageImage";
import ProductDetailsModal from "../ProductDetailsModal";

import {
  ActionButton,
  AvailableAvailabilityLabel,
  ComingSoonAvailabilityLabel,
  CustomImgixImage,
  CustomPodRating,
  InfoTypography,
  PriceTypography,
  SecondaryInfoTypography,
  SmallPodKeyInfo,
  TitleTypography,
  WeightTypography,
  WrapperLink,
  WrapperSkeleton,
  XpressAvailabilityLabel
} from "./styles";

type ProductCardVariantType = "SHOP" | "MY_PODS" | "SKELETON";

type Props = {
  variant?: ProductCardVariantType;
  product?: ProductListItemType;
  availability?: ProductAvailabilityType;
  price?: number; // Custom shop price, else fallback to DatoCMS product variant price
  podHistory?: PodHistoryType;
};

function ProductCard({
  variant = "SHOP",
  product,
  availability,
  price,
  podHistory
}: Props) {
  const theme = useTheme();
  const t = useTranslate();
  const isXpressEnabled = useFeatureFlag("SHOP_XPRESS");
  const isPriceEnabled = useFeatureFlag("SHOP_PRICE");

  if (!product && variant !== "SKELETON") {
    throw new Error(
      "You must set <ProductCard> variant to 'SKELETON' if you do not pass any product object."
    );
  }

  if (variant === "MY_PODS" && !podHistory) {
    throw new Error(
      "You must provide <ProductCard> a podHistory prop when using variant='MY_POD'."
    );
  }

  const productVariant = product ? getDefaultProductItemVariant(product) : null;
  if (product && !productVariant) {
    return null;
  }

  const variantDetail = productVariant?.variantDetail;

  const productCardPrice: PriceType = {
    amount:
      price !== undefined && isXpressEnabled ? price : productVariant?.price,
    currencyCode: "USD"
  };

  let cardWrapperLink = getProductDetailsPath(
    product?.productType,
    product.slug
  );
  if (variant === "MY_PODS") {
    if (product.disablePdp) {
      cardWrapperLink = `/rating/${podHistory.podKey}`;
    }
  }
  if (product.productType === "external") {
    cardWrapperLink = product.externalUrl;
  }

  const enableProductDetailsModal = !!product && variant === "SHOP";

  const [isProductDetailsModalOpen, setProductDetailsModalOpen] =
    useState(false);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      analyticsTrackProductClick(product);
      if (enableProductDetailsModal && product.productType !== "external") {
        event.preventDefault();
        setProductDetailsModalOpen(true);
      }
    },
    [product, enableProductDetailsModal]
  );

  const [inViewRef, inView] = useInView({
    triggerOnce: true
  });

  if (!inView || variant === "SKELETON") {
    return (
      <WrapperSkeleton
        className={`product-card${!isPriceEnabled ? " no-price" : ""}`}
        data-testid="product-card"
        ref={inViewRef}
      >
        <Box width="100%" paddingTop="100%" bgcolor="grey.300" />
      </WrapperSkeleton>
    );
  }

  return (
    <>
      {variant === "MY_PODS" && (
        <Helmet>
          <link
            href="https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@500&display=swap"
            rel="stylesheet"
          />
        </Helmet>
      )}

      <WrapperLink
        className="product-card"
        to={cardWrapperLink}
        // prettier-ignore
        title={`${productVariant?.title}${productVariant?.brand ? ` - ${productVariant?.brand.name}` : ""}`}
        onClick={handleClick}
        data-testid="product-card"
      >
        <ProductCard_Image
          productType={product?.productType}
          image={productVariant?.featuredImage?.url}
        />

        {/* Bottom of the card */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignSelf="stretch"
          borderTop={`1px solid ${theme.palette.grey[400]}`}
          borderBottom={`1px solid ${theme.palette.grey[400]}`}
          borderLeft={`1px solid ${theme.palette.grey[400]}`}
          borderRight={`1px solid ${theme.palette.grey[400]}`}
          borderRadius="0 0 3px 3px"
          position="relative"
        >
          {/* XPRESS/In Store/Coming Soon label */}
          {availability === "NO_ADDRESS" && isXpressEnabled && (
            <XpressAvailabilityLabel />
          )}
          {availability === "AVAILABLE" && isXpressEnabled && (
            <AvailableAvailabilityLabel />
          )}
          {availability === "COMING_SOON" && (
            <ComingSoonAvailabilityLabel productVariant={productVariant} />
          )}

          {/* Title & Brand */}
          <ProductCard_TitleBrand
            productCardVariant={variant}
            productVariant={productVariant}
          />

          {/* Mid rows: Product specifications, THC/CBD */}
          <ProductCard_Info
            productType={product?.productType}
            externalUrl={product?.externalUrl}
            variantDetail={variantDetail}
          />

          {/* Second row (Shop variant): AGX/In-store & Price/Grams */}
          {variant === "SHOP" && isPriceEnabled && (
            <ProductCard_AvailabilityPrice
              price={productCardPrice}
              weightText={getProductVariantWeightLabel(productVariant)}
            />
          )}

          {/* Second row (My pods variant): Pod key & unlocked date */}
          {variant === "MY_PODS" && podHistory && (
            <Box
              width="100%"
              display="flex"
              flexDirection={{ xs: "row", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              paddingY={{ xs: 1, sm: 1.5 }}
              borderTop={`1px solid ${theme.palette.grey[300]}`}
              paddingX={{ xs: 1, sm: 2 }}
            >
              {podHistory.podKey && (
                <SmallPodKeyInfo podKey={podHistory.podKey} />
              )}
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
                textAlign="right"
              >
                <InfoTypography>{t("myPods.unlockedOn")}</InfoTypography>
                <InfoTypography>
                  {moment(podHistory.createdDate).format("MMM Do YYYY")}
                </InfoTypography>
              </Box>
            </Box>
          )}

          {/* Third row (My pods variant): Leave a review or Pod rating + Shop link */}
          {variant === "MY_PODS" && (
            <Box
              width="100%"
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="center"
              alignItems={{ xs: "center", md: "flex-start" }}
            >
              {podHistory.userRating?.rating && (
                <ActionButton to={`/rating/${podHistory.podKey}`}>
                  <Rating
                    name="rating"
                    value={podHistory.userRating.rating}
                    readOnly
                    onClick={() => navigate(`/`)}
                  />
                </ActionButton>
              )}
              {!podHistory.userRating?.rating && (
                <ActionButton to={`/rating/${podHistory.podKey}`}>
                  {t("myPods.leaveReview")}
                </ActionButton>
              )}
              {!product.disablePdp && (
                <ActionButton
                  to={getProductDetailsPath(product.productType, product.slug)}
                >
                  {t("myPods.buyAgain")}
                </ActionButton>
              )}
            </Box>
          )}
        </Box>

        {/* Bottom AGX border when product is available for delivery */}
        {(availability === "NO_ADDRESS" || availability === "AVAILABLE") &&
          isXpressEnabled && (
            <Box
              bgcolor={theme.palette.expressDelivery}
              width="100%"
              minHeight={{ xs: "4px", sm: "6px" }}
              position="absolute"
              left={0}
              bottom={0}
            />
          )}
      </WrapperLink>

      {/* Product details modal */}
      {enableProductDetailsModal && (
        <ProductDetailsModal
          open={isProductDetailsModalOpen}
          onClose={() => setProductDetailsModalOpen(false)}
          productSelector={{
            id: product.originalId
          }}
        />
      )}
    </>
  );
}

export default React.memo(ProductCard, fastCompare);

// Product card is splitted into sub-components for better performance
const ProductCard_Image = React.memo(
  ({ productType, image }: { productType: string; image: string }) => {
    if (!image) return null;
    return (
      <>
        {/* Product image (Non-pods) */}
        {productType !== "oil" && (
          <CustomImgixImage
            src={image}
            width={424}
            height={424}
            imgixParams={{
              ar: "1",
              fit: "crop",
              auto: "format,compress"
            }}
            htmlAttributes={{
              draggable: false,
              loading: "lazy"
            }}
          />
        )}

        {/* Pod package image with yellow border */}
        {productType === "oil" && (
          <PodPackageImage
            artwork={{
              url: image,
              maxWidth: 424
            }}
          />
        )}
      </>
    );
  }
);

const ProductCard_TitleBrand = React.memo(
  ({
    productVariant,
    productCardVariant
  }: {
    productCardVariant: ProductCardVariantType;
    productVariant: ProductVariantListItemType;
  }) => {
    return (
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        alignItems="stretch"
        paddingTop={{ xs: 1, sm: 1 }}
        paddingBottom={{ xs: 1, sm: 1 }}
        paddingX={{ xs: 1, sm: 2 }}
      >
        {/* Title */}
        <TitleTypography
          variant="h6"
          style={{
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            maxWidth: "100%"
          }}
        >
          {productVariant?.title}
        </TitleTypography>

        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          marginTop={{ xs: 0.25, sm: 0.5 }}
        >
          {/* Brand */}
          {productVariant?.brand && (
            <SecondaryInfoTypography
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: "100%"
              }}
            >
              {productVariant?.brand.name}
            </SecondaryInfoTypography>
          )}

          {/* Rating */}
          {productCardVariant === "SHOP" && productVariant.rating && (
            <CustomPodRating rating={productVariant.rating} />
          )}
        </Box>
      </Box>
    );
  },
  fastCompare
);

const ProductCard_Info = React.memo(
  ({
    productType,
    externalUrl,
    variantDetail
  }: {
    productType: string;
    externalUrl?: string;
    variantDetail: Queries.DatoCmsProductVariantItemFragmentFragment["variantDetail"];
  }) => {
    const t = useTranslate();
    const theme = useTheme();

    const coaReport = variantDetail?.coaReport;
    const thcPercent = coaReport?.thcTotalPercent;
    const secondaryCannabinoidLabel = coaReport?.secondaryCannabinoidLabel;
    const secondaryCannabinoidPercent =
      coaReport?.secondaryCannabinoidTotalPercentText || "0%";

    const aromas = variantDetail?.aromas?.slice(0, 3);
    const effects = variantDetail?.effects?.slice(0, 3);

    if (productType === "external") {
      return (
        <Box
          flex={1}
          width="100%"
          borderTop={`1px solid ${theme.palette.grey[300]}`}
          paddingY={{ xs: 0.5, sm: 0.75 }}
          paddingX={{ xs: 1, sm: 2 }}
          display="flex"
          alignItems="center"
          flexDirection="row"
          textAlign="left"
        >
          <SecondaryInfoTypography>
            {`${t("shop.externalProductAvailableAt")} ${getDomainFromUrl(
              externalUrl
            )}`}
          </SecondaryInfoTypography>
        </Box>
      );
    }

    return (
      <>
        {/* Oil strain + extraction type row */}
        <Box
          flex={1}
          width="100%"
          borderTop={`1px solid ${theme.palette.grey[300]}`}
          paddingY={{ xs: 0.5, sm: 0.75 }}
          paddingX={{ xs: 1, sm: 2 }}
          display="flex"
          alignItems="center"
          flexDirection="row"
          textAlign="left"
        >
          {productType === "oil" && (
            <>
              {(variantDetail?.plantSpecies || variantDetail?.oilType) && (
                <>
                  {variantDetail?.plantSpecies && (
                    <InfoTypography component="span" style={{ flexShrink: 0 }}>
                      {t(`plantSpecies.${variantDetail.plantSpecies}`)}
                    </InfoTypography>
                  )}
                  <SeparatorBullet />
                  {variantDetail?.oilType && (
                    <InfoTypography component="span">
                      {t(`oilType.${variantDetail.oilType}`)}
                    </InfoTypography>
                  )}
                </>
              )}
            </>
          )}

          {productType === "device" && (
            <InfoTypography>
              {t("shop.battery.description.line1")}
            </InfoTypography>
          )}
        </Box>

        {/* THC/CBD row */}
        <Box
          flex={1}
          width="100%"
          borderTop={`1px solid ${theme.palette.grey[300]}`}
          paddingY={{ xs: 0.5, sm: 0.75 }}
          paddingX={{ xs: 1, sm: 2 }}
          display="flex"
          flexDirection="row"
          alignItems="center"
          textAlign="left"
        >
          {productType === "oil" && (
            <>
              {thcPercent && (
                <>
                  {/* Oil THC percentage */}
                  <SecondaryInfoTypography
                    component="span"
                    style={{ flexShrink: 0 }}
                  >
                    {/* Δ9- (Hidden on mobile) */}
                    <Box
                      component="span"
                      display={{ xs: "none", sm: "inline" }}
                    >{`Δ9-`}</Box>
                    <span>{`THC:`}</span>
                  </SecondaryInfoTypography>
                  &nbsp;
                  <InfoTypography component="span" style={{ flexShrink: 0 }}>
                    {formatTotalPercentage(thcPercent)}
                  </InfoTypography>
                  <SeparatorBullet />
                  {/* Oil CBD percentage */}
                  <SecondaryInfoTypography component="span">
                    {`${secondaryCannabinoidLabel || "CBD"}:`}
                  </SecondaryInfoTypography>
                  &nbsp;
                  <InfoTypography component="span">
                    {`${secondaryCannabinoidPercent}`}
                  </InfoTypography>
                </>
              )}

              {!thcPercent && (
                <SecondaryInfoTypography>
                  {t("shop.moreDetailsSoon")}
                </SecondaryInfoTypography>
              )}
            </>
          )}

          {productType === "device" && (
            <InfoTypography>
              {t("shop.battery.description.line2")}
            </InfoTypography>
          )}
        </Box>

        {/* Aromas/Effects icons row */}
        <Box
          flex={1}
          width="100%"
          borderTop={`1px solid ${theme.palette.grey[300]}`}
          paddingY={
            aromas?.length > 0 && effects?.length > 0
              ? { xs: 0.75, sm: 1 }
              : { xs: 0.5, sm: 0.75 }
          }
          paddingX={{ xs: 1, sm: 2 }}
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          textAlign="left"
          justifyContent="center"
        >
          {productType === "oil" && (
            <>
              {(aromas?.length > 0 || effects?.length > 0) && (
                <>
                  {/* Aromas */}
                  {aromas?.length > 0 && (
                    <Box display="flex" alignItems="center">
                      {aromas.map((a, i) => (
                        <Box
                          key={a.title}
                          component="span"
                          alignItems="center"
                          display={{
                            xs: i === 2 ? "none" : "flex",
                            sm: "flex"
                          }}
                        >
                          {i !== 0 && <SeparatorBullet />}
                          <OilAromaIcon aroma={a} />
                          <InfoTypography component="span">
                            {capitalize(a.title)}
                          </InfoTypography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Effects */}
                  {effects?.length > 0 && (
                    <Box
                      display="flex"
                      alignItems="center"
                      marginTop={{ xs: 0.5, md: 0.5 }}
                    >
                      {effects.map((e, i) => (
                        <Box
                          key={e.name}
                          component="span"
                          alignItems="center"
                          display={{
                            xs: i === 2 ? "none" : "flex",
                            sm: "flex"
                          }}
                        >
                          {i !== 0 && <SeparatorBullet />}
                          <OilEffectIcon effect={e} />
                          <InfoTypography component="span">
                            {e.name}
                          </InfoTypography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}

              {!aromas?.length && !effects?.length && (
                <SecondaryInfoTypography>
                  {t("shop.moreDetailsSoon")}
                </SecondaryInfoTypography>
              )}
            </>
          )}

          {productType === "device" && (
            <InfoTypography>
              {t("shop.battery.description.line3")}
            </InfoTypography>
          )}
        </Box>

        {productType === "device" && (
          <Box
            flex={1}
            width="100%"
            borderTop={`1px solid ${theme.palette.grey[300]}`}
            paddingY={{ xs: 0.5, sm: 0.75 }}
            paddingX={{ xs: 1, sm: 2 }}
            display="flex"
            flexDirection="row"
            alignItems="center"
            textAlign="left"
          >
            <InfoTypography>
              {t("shop.battery.description.line4")}
            </InfoTypography>
          </Box>
        )}
      </>
    );
  },
  fastCompare
);

const ProductCard_AvailabilityPrice = React.memo(
  ({ price, weightText }: { price: PriceType; weightText?: string }) => {
    const theme = useTheme();
    return (
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        justifyContent="space-between"
        alignItems="center"
        paddingTop={{ xs: 1.25, sm: 1.5 }}
        paddingBottom={{ xs: 1.25, sm: 2 }}
        paddingX={{ xs: 1, sm: 2 }}
        borderTop={`1px solid ${theme.palette.grey[300]}`}
      >
        {/* Price + Weight */}
        <Box
          display="flex"
          justifyContent={{ xs: "space-between", sm: "flex-start" }}
          flex={1}
        >
          <Box display="flex" alignItems="baseline">
            {/* Price */}
            {price && (
              <PriceTypography component="span">
                {formatPrice(price, {
                  hideCurrencyCode: true
                })}
              </PriceTypography>
            )}
          </Box>
        </Box>

        {/* Weight (Ex 1g) */}
        <WeightTypography>{weightText}</WeightTypography>
      </Box>
    );
  },
  fastCompare
);

function getDomainFromUrl(url: string): string | null {
  try {
    const domain = new URL(url).hostname;
    return domain;
  } catch (error) {
    return null;
  }
}
